import React, { useMemo, useState } from 'react';
import { CalendarClock, X } from 'lucide-react';
import Button from '../../../shared/components/ui/Button';
import AiLanguageSelect, { languageDisplay } from './AiLanguageSelect';
import {
  buildScheduleDateOptions,
  buildScheduleTimeSlots,
  toScheduledAtIso
} from './aiScheduleTime';

export default function AiScheduleSendDrawer({
  open,
  onClose,
  channel = 'whatsapp',
  title,
  recipientName,
  recipientLabel,
  scheduleSuggestion,
  tone,
  templateKey,
  templateOptions,
  toneOptions,
  message,
  subject = '',
  sessionOpen = true,
  canSendAsTemplate = false,
  selectedMetaTemplateName = '',
  onToneChange,
  onTemplateChange,
  onMessageChange,
  onSubjectChange,
  onRegenerate,
  onSchedule,
  targetLanguage = 'en',
  onTargetLanguageChange,
  translationLanguages,
  showTranslation = true,
  originalEnglishMessage = '',
  onTranslate,
  isTranslating = false,
  isRegenerating,
  isScheduling
}) {
  const timeSlots = useMemo(() => buildScheduleTimeSlots(), []);
  const dateOptions = useMemo(() => buildScheduleDateOptions(90), []);

  const initial = useMemo(() => {
    if (scheduleSuggestion?.suggestedDate && scheduleSuggestion?.suggestedTime) {
      return {
        date: scheduleSuggestion.suggestedDate,
        time: scheduleSuggestion.suggestedTime
      };
    }
    return { date: dateOptions[0], time: '11:00' };
  }, [scheduleSuggestion, dateOptions]);

  const [date, setDate] = useState(initial.date);
  const [time, setTime] = useState(initial.time);

  React.useEffect(() => {
    if (open) {
      setDate(initial.date);
      setTime(initial.time);
    }
  }, [open, initial.date, initial.time]);

  if (!open) return null;

  const scheduledAtIso = toScheduledAtIso(date, time);
  const channelLabel = channel === 'email' ? 'Email' : 'WhatsApp';

  return (
    <div className="fixed inset-0 z-[1200] flex justify-end bg-black/30">
      <div className="relative flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-5 py-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {title || `Schedule ${channelLabel}`}
            </h3>
            <p className="text-xs text-gray-500">AI-framed message · {recipientName || 'Lead'}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 hover:bg-gray-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {scheduleSuggestion?.reason ? (
            <div className="mb-4 flex gap-2 rounded-xl border border-teal-100 bg-teal-50/60 px-3 py-3 text-xs leading-relaxed text-teal-950">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-[#1E4A53]" />
              <span>{scheduleSuggestion.reason}</span>
            </div>
          ) : null}

          <div className="mb-4 grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Date
              </label>
              <select
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {dateOptions.map((d) => (
                  <option key={d} value={d}>
                    {d}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Time (11 AM – 5 PM)
              </label>
              <select
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                {timeSlots.map((slot) => (
                  <option key={slot} value={slot}>
                    {slot}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {recipientLabel ? (
            <p className="mb-3 text-xs text-gray-500">{recipientLabel}</p>
          ) : null}

          {channel === 'email' ? (
            <div className="mb-4">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                Subject
              </label>
              <input
                value={subject}
                onChange={(e) => onSubjectChange?.(e.target.value)}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
          ) : (
            <>
              <div className="mb-4">
                <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Tone
                </p>
                <div className="flex flex-wrap gap-2">
                  {toneOptions?.map((option) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => onToneChange?.(option.id)}
                      className={`rounded-lg px-3 py-1.5 text-xs font-medium ${
                        tone === option.id
                          ? 'bg-[#1E4A53] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}>
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="mb-4">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Follow-up type
                </label>
                <select
                  value={templateKey}
                  onChange={(e) => onTemplateChange?.(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  {templateOptions?.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              {!sessionOpen && canSendAsTemplate ? (
                <p className="mb-3 text-[11px] text-amber-800">
                  Session closed — will send via template{' '}
                  {selectedMetaTemplateName ? `(${selectedMetaTemplateName})` : ''} at schedule time.
                </p>
              ) : null}
            </>
          )}

          {channel === 'whatsapp' && showTranslation ? (
            <>
              <div className="mb-4 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                <AiLanguageSelect
                  value={targetLanguage}
                  onChange={onTargetLanguageChange}
                  disabled={isTranslating || isRegenerating}
                  languages={translationLanguages}
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="h-[42px] whitespace-nowrap"
                  disabled={
                    isTranslating ||
                    isRegenerating ||
                    targetLanguage === 'en' ||
                    !originalEnglishMessage?.trim()
                  }
                  onClick={onTranslate}>
                  {isTranslating ? 'Translating…' : 'Translate'}
                </Button>
              </div>

              {targetLanguage !== 'en' && originalEnglishMessage?.trim() ? (
                <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-xs leading-relaxed text-gray-600">
                  <p className="mb-1 font-semibold uppercase tracking-wide text-gray-500">
                    English original
                  </p>
                  <p>{originalEnglishMessage}</p>
                </div>
              ) : null}

              {targetLanguage !== 'en' ? (
                <p className="mb-3 text-[11px] text-teal-800">
                  Preview & schedule in {languageDisplay(targetLanguage)}. English is kept on
                  file.
                </p>
              ) : null}
            </>
          ) : null}

          <div className="mb-2">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">
              Message preview
            </label>
            <textarea
              value={message}
              onChange={(e) => onMessageChange?.(e.target.value)}
              rows={8}
              disabled={isRegenerating}
              className="w-full rounded-xl border border-gray-300 px-3 py-3 text-sm leading-relaxed disabled:bg-gray-50"
            />
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t px-5 py-4">
          <div className="flex items-center gap-2">
            <Button
              displayType="secondary"
              type="button"
              onClick={onRegenerate}
              disabled={isRegenerating}>
              Regenerate
            </Button>
            <Button
              type="button"
              onClick={() =>
                onSchedule?.({
                  scheduledAt: scheduledAtIso,
                  sendMode:
                    channel === 'whatsapp' && !sessionOpen && canSendAsTemplate
                      ? 'template'
                      : 'text',
                  metaTemplateName: selectedMetaTemplateName || undefined
                })
              }
              disabled={isScheduling || !message?.trim() || (channel === 'email' && !subject?.trim())}
              className="flex-1 !bg-[#1E4A53]">
              {isScheduling ? 'Scheduling…' : `Schedule ${channelLabel}`}
            </Button>
          </div>
          <p className="text-[11px] text-gray-500">
            Sends automatically at {date} {time} IST. Logged in Sales Log when delivered.
          </p>
        </div>
      </div>
    </div>
  );
}
